import { useState, useEffect, useCallback } from 'react'
import {
  Users, FileText, Shield, AlertTriangle, Clock, CheckCircle2,
  ArrowUpRight, Activity, Globe, Server, Loader, XCircle, RefreshCw,
  Sparkles, Layers
} from 'lucide-react'
import { getClients, getReports, getAuditLogs, getAgents } from '../api'

export default function Dashboard({ onNavigate, onPendingChange }) {
  const [clients, setClients] = useState([])
  const [reports, setReports] = useState([])
  const [auditEvents, setAuditEvents] = useState([])
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [clientData, reportData, auditData, agentData] = await Promise.all([
        getClients(),
        getReports(),
        getAuditLogs(),
        getAgents().catch(() => ({ agents: [] })),
      ])
      const clientList = clientData.clients || []
      const reportList = reportData.reports || []
      const auditList = (auditData.audit_logs || []).slice(0, 8)
      setClients(clientList)
      setReports(reportList)
      setAuditEvents(auditList)
      setAgents(agentData?.agents || [])
      const pending = reportList.filter(r => r.status === 'draft').length
      onPendingChange?.(pending)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [onPendingChange])

  useEffect(() => { fetchAll() }, [fetchAll])

  const totalResources = Math.max(
    clients.reduce((sum, c) => sum + (c.resources?.length || 0), 0),
    agents.length
  )
  const pendingReports = reports.filter(r => r.status === 'draft').length
  const sentReports = reports.filter(r => r.status === 'dispatched' || r.status === 'sent').length
  const recentReports = [...reports].slice(0, 5)

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, gap: 12 }}>
      <Loader size={36} className="spin" style={{ color: 'var(--brand-blue)' }} />
      <div className="text-muted">Loading live cloud operations data...</div>
    </div>
  )

  if (error) return (
    <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
      <XCircle size={32} style={{ color: 'var(--brand-coral)', margin: '0 auto 12px' }} />
      <h3 style={{ color: 'var(--brand-coral)', fontFamily: 'var(--font-serif)', marginBottom: 8 }}>Backend Connection Failed</h3>
      <p className="text-muted text-sm" style={{ marginBottom: 16 }}>{error}</p>
      <button className="btn btn-primary" onClick={fetchAll}>Retry Connection</button>
    </div>
  )

  return (
    <div className="animate-in">
      {/* Brand Header Banner */}
      <div style={{
        background: '#FFFFFF',
        border: '2px solid var(--border-ink)',
        borderRadius: 14,
        padding: '20px 24px',
        marginBottom: 24,
        boxShadow: 'var(--shadow-solid-md)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img
            src="/logo.png"
            alt="CloudIQ Studio"
            style={{
              width: 50,
              height: 50,
              objectFit: 'contain',
              background: '#FAF8F5',
              borderRadius: 10,
              padding: 3,
              border: '1.5px solid var(--border-ink)',
              boxShadow: '2px 2px 0px var(--border-ink)'
            }}
          />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 19, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
                Managed Cloud Operations Hub
              </h2>
              <span className="badge badge-info" style={{ fontSize: 10 }}>
                Live Monitoring
              </span>
            </div>
            <p style={{ margin: '2px 0 0 0', color: 'var(--ink-600)', fontSize: 12.5 }}>
              Continuous multi-cloud telemetry across <strong>{clients.length} accounts</strong> and <strong>{totalResources} EC2 nodes</strong>.
            </p>
          </div>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => onNavigate('quick-report')}
          style={{ padding: '9px 18px', fontSize: 13 }}
        >
          Executive Report Studio <ArrowUpRight size={15} />
        </button>
      </div>

      {/* Stat Cards */}
      <div className="stat-grid">
        <div className="stat-card" onClick={() => onNavigate('clients')} style={{ cursor: 'pointer' }}>
          <div className="stat-card-icon blue"><Users size={20} /></div>
          <div className="stat-card-value">{clients.length}</div>
          <div className="stat-card-label">Active Clients</div>
          {clients.length > 0 && <div className="stat-card-delta up"><ArrowUpRight size={12} /> Active</div>}
        </div>

        <div className="stat-card">
          <div className="stat-card-icon teal"><Server size={20} /></div>
          <div className="stat-card-value">{totalResources}</div>
          <div className="stat-card-label">Monitored Fleet Nodes</div>
        </div>

        <div className="stat-card" onClick={() => onNavigate('reports')} style={{ cursor: 'pointer' }}>
          <div className="stat-card-icon yellow"><FileText size={20} /></div>
          <div className="stat-card-value">{pendingReports}</div>
          <div className="stat-card-label">Pending Reports</div>
          {pendingReports > 0 && <div className="stat-card-delta down"><Clock size={12} /> Review Needed</div>}
        </div>

        <div className="stat-card">
          <div className="stat-card-icon green"><CheckCircle2 size={20} /></div>
          <div className="stat-card-value">{sentReports}</div>
          <div className="stat-card-label">Reports Dispatched</div>
        </div>
      </div>

      <div className="grid-2-1">
        {/* Recent Reports */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Executive Reports</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('reports')}>
              View All <ArrowUpRight size={14} />
            </button>
          </div>
          {recentReports.length === 0 ? (
            <div className="empty-state">
              <FileText size={36} style={{ color: 'var(--brand-blue)', margin: '0 auto 8px' }} />
              <h4 style={{ fontFamily: 'var(--font-serif)', color: 'var(--ink-900)' }}>No Reports Yet</h4>
              <p style={{ fontSize: 12.5 }}>Generate your first executive report in the Fast-Track Studio.</p>
              <button className="btn btn-primary btn-sm" onClick={() => onNavigate('quick-report')} style={{ marginTop: 8 }}>
                Open Studio
              </button>
            </div>
          ) : (
            <div style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client Account</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Alarm Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReports.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{r.client_name}</td>
                      <td className="font-mono text-xs text-muted">{r.report_date}</td>
                      <td>
                        <span className={`badge ${r.status === 'sent' ? 'badge-ok' : 'badge-warning'}`}>
                          {r.status === 'sent' ? 'Sent' : 'Draft'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${r.alarm_summary?.includes('IN ALARM') && !r.alarm_summary?.startsWith('0') ? 'badge-alarm' : 'badge-ok'}`}>
                          {r.alarm_summary || 'All OK'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity Feed */}
        <div className="card">
          <div className="card-header">
            <h3>Live Security & Action Log</h3>
            <button className="btn-icon" title="Refresh" onClick={fetchAll}><RefreshCw size={14} /></button>
          </div>
          <div className="card-body" style={{ padding: 14 }}>
            {auditEvents.length === 0 ? (
              <div className="empty-state" style={{ padding: '24px 10px' }}>
                <Activity size={28} style={{ color: 'var(--ink-400)', margin: '0 auto 8px' }} />
                <div style={{ fontSize: 12.5, color: 'var(--ink-500)' }}>No platform activity recorded yet.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {auditEvents.map((evt, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: 'var(--canvas-bg)',
                      border: '1px solid var(--border-ink)',
                      boxShadow: 'var(--shadow-solid-sm)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <span className="badge badge-stable" style={{ fontSize: 9.5 }}>{evt.action}</span>
                      <span className="font-mono text-xs text-muted">
                        {evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-800)', marginTop: 4 }}>
                      {evt.details}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
